package com.wintermar.inspection.core.ui.component.button

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.TextBodyTiny
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.icon_button
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

data class IconButtonDefaultContent(
    val width: Dp,
    val height: Dp,
    val borderWidth: Dp,
    val borderColor: Color = Color.Unspecified,
    val label: String? = null,
    val description: String? = null,
    val labelColor: Color,
    val radius: Dp,
    val padding: Dp
)

@Composable
fun iconButtonContent(): IconButtonDefaultContent {
    val dimension = LocalAppTheme.current.dimension
    return IconButtonDefaultContent(
        width = dimension.size.icon.s,
        height = dimension.size.icon.s,
        borderWidth = dimension.spacing.space0,
        labelColor = LocalAppTheme.current.color.text.default,
        radius = LocalAppTheme.current.dimension.radius.md,
        padding = LocalAppTheme.current.dimension.spacing.space1
    )
}

@Composable
fun IconButton(
    icon: DrawableResource,
    backgroundColor: Color,
    tint: Color,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    contentDescription: String = "",
    contentValue: IconButtonDefaultContent = iconButtonContent(),
    onClick: () -> Unit
) {
    val color = LocalAppTheme.current.color
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(
            space = LocalAppTheme.current.dimension.spacing.space2,
            alignment = Alignment.CenterVertically
        ),
        modifier = modifier
            .then(
                if (contentValue.borderWidth != LocalAppTheme.current.dimension.spacing.space0) {
                    Modifier.border(
                        width = contentValue.borderWidth,
                        color = if (enabled) contentValue.borderColor else color.border.default,
                        shape = RoundedCornerShape(contentValue.radius)
                    )
                } else Modifier
            )
            .clip(shape = RoundedCornerShape(contentValue.radius))
            .background(
                if (enabled || backgroundColor == Color.Transparent) backgroundColor
                else color.surface.input.disabled
            )
            .clickable(enabled = enabled, onClick = onClick)
            .padding(contentValue.padding)
    ) {
        Icon(
            painter = painterResource(icon),
            tint = if (enabled) tint else color.icon.disabled,
            modifier = Modifier
                .width(if (contentValue.label == null) contentValue.width / 1.2f else contentValue.width)
                .height(if (contentValue.label == null) contentValue.height / 1.2f else contentValue.height),
            contentDescription = contentDescription.ifEmpty { stringResource(Res.string.icon_button) }
        )

        contentValue.label?.let {
            TextBodySmall(
                text = it,
                bold = true,
                color = {
                    if (enabled) contentValue.labelColor else color.text.disabled
                },
                textAlign = TextAlign.Center
            )
        }

        contentValue.description?.let {
            TextBodyTiny(
                text = it,
                color = {
                    if (enabled) it.default else color.text.disabled
                },
                textAlign = TextAlign.Center
            )
        }
    }
}
